import { useEffect, useRef, useState } from 'react';

import { Box, Grid, Typography, useTheme } from '@mui/material';

import { ContentTypeRichTextContent, MultiCardV2CardsCollection } from '@/graphql/__generated/sdk';
import ContentTypeRichText from '@/components/ContentTypeRichText';
import ContentSection from '@/components/ContentSection';
import { ContentSectionProps } from '@/components/ContentSection/ContentSection';
import MultiCardV2 from '@/components/MultiCard';
import { getBlogListByBrand } from '@/services/getBlogListByBrand';
import { useAppContext } from '@/context';
import { logWarning } from '@/utils/miscUtils';
import { ComponentResolver } from '@/components/ComponentResolver';

import BlogDetailsHero from '../BlogDetailsHero';
import { IBlogDetailsPage } from '../BlogDetailsPage';
import SocialMediaShareLinks from '../SocialMediaShareLinks';
import { socialMediaLinkStyles } from '../BlogDetailsPage.styles';
import { lovetBlogSocialMediaLinks } from '../SocialMediaLinks';
import BlogAuthorDetails from '../BlogAuthorDetails';
import { BLOG_AUTHOR_LABELS } from '../BlogDetailsPage.constants';

import {
	blogDetailsCategoryPillStyle,
	blogDetailsPageFullStyles,
	blogDetailsPageFullTextStyles,
	blogDetailsPageInfoBarStyles,
	blogDetailsPageMainColumnStyles,
} from './BlogDetailsPageFull.styles';
import BlogDetailsPageFullSideNav from './BlogDetailsPageFullSideNav';
import BlogTags from '../BlogTags';
import {
	NUMBER_OF_RELATED_BLOGS,
	SKIP_RELATED_BLOGS,
	getNewTopPosition,
	mapRelatedBlogs,
	emptyMetaData,
} from './BlogDetailsPage.helpers';

interface IBlogDetailsPageFullProps extends IBlogDetailsPage {
	isSmallScreen: boolean;
}

export interface IBlogDetailsPageNavClickProps {
	e: React.MouseEvent<HTMLAnchorElement>;
	which: number | string;
	offset?: number;
}

export default function BlogDetailsPageFull(props: Readonly<IBlogDetailsPageFullProps>) {
	const {
		text,
		author,
		reviewer,
		category,
		subCategory,
		isSmallScreen,
		relatedBlogsContentSection,
		additionalContentCollection,
		preview = false,
		brand,
		tags,
	} = props;
	const categoryName = category?.name;
	const subCategoryName = subCategory?.name;
	const showTagItems = !!(tags && tags.length > 0);
	const hasCategoryItems = !!(categoryName ?? subCategoryName);
	const showCategoryItems = !showTagItems && hasCategoryItems;

	const theme = useTheme();
	const { environment, config } = useAppContext();
	const contentfulGraphqlURL =
		environment === 'prod'
			? config.services.prod.CONTENTFUL_GRAPHQL_URL
			: config.services.nonprod.CONTENTFUL_GRAPHQL_URL;
	// brand name comes from blog list content itself or take it from context
	const brandNameForQuery = brand?.title ?? config.name;
	const additionalContentItems = additionalContentCollection?.items ?? [];

	const bodyTextRef = useRef(null);

	const [relatedBlogsCards, setRelatedBlogsCards] = useState<MultiCardV2CardsCollection>();
	const [isRelatedBlogsFetched, setIsRelatedBlogsFetched] = useState(false);

	const handleNavClick = ({ e, which, offset = 0 }: IBlogDetailsPageNavClickProps) => {
		e.preventDefault();
		if (!bodyTextRef?.current) return;
		const element = bodyTextRef.current as HTMLElement;
		const target = element.querySelectorAll('h2')[which as number];
		if (target) {
			const y = getNewTopPosition(target, offset);
			window.scrollTo({ top: y, behavior: 'smooth' });
		}
	};

	useEffect(() => {
		const fetchRelatedBlogs = async () => {
			try {
				if (!categoryName) return;
				const relatedBlogsResponse = await getBlogListByBrand(
					NUMBER_OF_RELATED_BLOGS,
					SKIP_RELATED_BLOGS,
					brandNameForQuery,
					contentfulGraphqlURL,
					preview,
					categoryName
				);

				if (relatedBlogsResponse) {
					const mappedRelatedBlogs = mapRelatedBlogs(
						relatedBlogsResponse.blogDetailsPageCollection,
						config,
						isSmallScreen
					);
					setRelatedBlogsCards(mappedRelatedBlogs);
				}
			} catch (error) {
				// Handle any errors that occurred during the API call.
				// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				logWarning(`Error fetching related blogs list by brand error: ${error}`);
			} finally {
				setIsRelatedBlogsFetched(true);
			}
		};

		if (relatedBlogsContentSection && !isRelatedBlogsFetched) {
			void fetchRelatedBlogs();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [relatedBlogsContentSection, categoryName, isRelatedBlogsFetched]);

	return (
		<Box data-testid="blog_detail_page_container_full">
			<BlogDetailsHero {...props} isSmallScreen={isSmallScreen} theme={theme} />
			<Box sx={blogDetailsPageFullStyles}>
				{text && (
					<BlogDetailsPageFullSideNav
						text={text as ContentTypeRichTextContent}
						handleNavClick={handleNavClick}
						isSmallScreen={isSmallScreen}
					/>
				)}
				<Box sx={blogDetailsPageMainColumnStyles}>
					<Grid container sx={blogDetailsPageInfoBarStyles}>
						{showTagItems && <BlogTags tags={tags} />}
						{showCategoryItems && (
							<Box data-testid="blog_detail_page_categories">
								{category?.name && (
									<Typography
										sx={blogDetailsCategoryPillStyle}
										variant={'bodySmallSemiBold'}
										color={'text.interactive'}
									>
										{category.name}
									</Typography>
								)}
								{subCategory?.name && (
									<Typography
										sx={blogDetailsCategoryPillStyle}
										marginLeft={1}
										variant={'bodySmallSemiBold'}
										color={'text.interactive'}
									>
										{subCategory.name}
									</Typography>
								)}
							</Box>
						)}
						<Box sx={socialMediaLinkStyles(0.75)} data-testid="blog_detail_page_social">
							<SocialMediaShareLinks
								isCompactDesign={true}
								mediaLinks={lovetBlogSocialMediaLinks}
								iconColor={theme.palette.text.interactive}
								hideIconTitleOnMobile={true}
							/>
						</Box>
					</Grid>
					<ContentTypeRichText
						content={text as ContentTypeRichTextContent}
						dataTestId="blog_detail_page_content_text_richtext"
						contentMaxWidth="100%"
						disableGutters={true}
						disablePaddingOn={['Top', 'Bottom']}
						containerSx={blogDetailsPageFullTextStyles}
						backgroundColor="transparent"
						containerRef={bodyTextRef}
					/>
					{author && <BlogAuthorDetails {...author} sectionLabel={BLOG_AUTHOR_LABELS.WRITTEN_BY} />}
					{reviewer && (
						<BlogAuthorDetails {...reviewer} sectionLabel={BLOG_AUTHOR_LABELS.MEDICALLY_REVIEWED_BY} />
					)}
				</Box>
			</Box>
			{relatedBlogsContentSection && relatedBlogsCards?.items?.length && (
				<>
					<Box data-testid="blog_detail_page_related_blogs">
						<ContentSection {...(relatedBlogsContentSection as ContentSectionProps)} />
					</Box>
					<MultiCardV2
						{...emptyMetaData}
						cardsCollection={relatedBlogsCards}
						enableResourceCardMode
						forMobile="carousel"
						enableMobileCarouselNavigation
					/>
				</>
			)}
			{additionalContentItems.length > 0 &&
				additionalContentItems.map((content, i) => {
					if (!content?.__typename) return null;
					return (
						<Grid
							item
							key={content._id}
							data-testid={`section_blog_details_page_additional_content_${i + 1}`}
						>
							<ComponentResolver
								componentProps={{
									...content,
									display: 'Both',
									variant: 'default',
								}}
								typename={content.__typename}
							/>
						</Grid>
					);
				})}
		</Box>
	);
}
